import React, { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChart3 } from 'lucide-react';
import { ZijMenu, ZijMenuMobielKnop, PaginaKop, type ZijMenuItem } from '../components/ZijMenu';
import { ONDERDELEN, type Onderdeel } from '../services/professionalDataApi';
import { PrivacyNoot } from '../components/professionalData/Bediening';
import { BeroepsregistratieOnderdeel } from '../components/professionalData/BeroepsregistratieOnderdeel';
import { BedrijfscijfersOnderdeel } from '../components/professionalData/BedrijfscijfersOnderdeel';
import { MeldingsmonitorOnderdeel } from '../components/professionalData/MeldingsmonitorOnderdeel';
import { KwaliteitOnderdeel } from '../components/professionalData/KwaliteitOnderdeel';
import { MarktOnderdeel } from '../components/professionalData/MarktOnderdeel';

/**
 * Hoofdmenu Data (Menu 4) van de professional: de Analytics Control Tower met
 * een vast, genummerd submenu 1–5 onder /professional/data/:onderdeel.
 * Elk onderdeel heeft zijn eigen endpoint (src/routes/professionalData.ts).
 * Case-registratie & jaarverslag blijft een eigen werkpagina.
 */

export const DATA_PAD = '/professional/data';

const nummerIcoon = (n: number): React.FC<{ className?: string }> => {
  const Icoon: React.FC<{ className?: string }> = ({ className = '' }) => (
    <span className={`${className} inline-flex items-center justify-center rounded-md bg-navy-900 text-[11px] font-bold leading-none text-white`} aria-hidden>
      {n}
    </span>
  );
  return Icoon;
};
const NUMMER_ICONEN = ONDERDELEN.map((_, i) => nummerIcoon(i + 1));

const INHOUD: Record<Onderdeel, React.FC> = {
  beroepsregistratie: BeroepsregistratieOnderdeel,
  bedrijfscijfers: BedrijfscijfersOnderdeel,
  meldingsmonitor: MeldingsmonitorOnderdeel,
  kwaliteit: KwaliteitOnderdeel,
  markt: MarktOnderdeel,
};

const ProfessionalDataPage: React.FC = () => {
  const { t } = useTranslation('professionalData');
  const { onderdeel } = useParams<{ onderdeel: string }>();
  const [open, setOpen] = useState(false);

  if (!onderdeel || !(ONDERDELEN as readonly string[]).includes(onderdeel)) {
    return <Navigate to={`${DATA_PAD}/beroepsregistratie`} replace />;
  }
  const actief = onderdeel as Onderdeel;
  const Inhoud = INHOUD[actief];

  const items: ZijMenuItem[] = ONDERDELEN.map((o, i) => ({
    key: o,
    label: `${i + 1}. ${t(`menu.${o}`)}`,
    omschrijving: t(`menu.${o}Desc`),
    icon: NUMMER_ICONEN[i],
    href: `${DATA_PAD}/${o}`,
    actief: o === actief,
  }));

  return (
    <div className="bg-gray-50">
      <ZijMenuMobielKnop onClick={() => setOpen(true)} />
      <div className="max-w-7xl mx-auto flex min-h-[calc(100vh-4rem)]">
        <ZijMenu
          titel={t('menuTitel')}
          ondertitel={t('menuOndertitel')}
          groepen={[
            { items },
            { label: t('menu.werkpaginas'), items: [{ key: 'jaarverslag', label: t('menu.caseRegistratie'), icon: BarChart3, href: '/professional/jaarverslag' }] },
          ]}
          mobielOpen={open}
          onMobielSluit={() => setOpen(false)}
        />
        <div className="flex-1 min-w-0">
          <PaginaKop
            label={`4. ${t('menuTitel')} · ${ONDERDELEN.indexOf(actief) + 1}`}
            titel={t(`${actief}.titel`)}
            ondertitel={t(`${actief}.ondertitel`)}
            className="px-4 sm:px-6 pt-6"
          />
          <div className="p-4 sm:p-6 space-y-6">
            <Inhoud />
            <PrivacyNoot />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalDataPage;
